import type {DropResult} from '@hello-pangea/dnd';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {ActionIcon, Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  IconChevronDown,
  IconChevronRight,
  IconGripVertical,
  IconTrash,
} from '@tabler/icons-react';
import React, {useEffect, useRef, useState} from 'react';
import type {CourseDefinitionQuizQuestionResponse} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import EditCourseDefinitionQuestion from 'src/pages/Modules/Training/shared/editCourseDefinition/EditCourseDefinitionQuestion';
import type {QuizQuestionWithKey} from 'src/app/training/courseDefaults';
import {
  CreateDefaultCourseDefinitionQuizQuestion,
  getQuestionKey,
} from 'src/app/training/courseDefaults';

export interface QuizQuestionsCanvasProps {
  questions: QuizQuestionWithKey[];
  disabled?: boolean;
  onQuestionsChange: (questions: QuizQuestionWithKey[]) => void;
}

const correctAnswerCount = (
  question: CourseDefinitionQuizQuestionResponse
): number => (question.answers ?? []).filter(answer => answer.isCorrect).length;

/**
 * The quiz's questions as an inline, drag-reorderable stack of expandable
 * cards beneath the quiz-level fields — replacing the previous split where a
 * question was an outline node whose editor *replaced* the quiz editor, so the
 * quiz and its questions were never visible at the same time.
 *
 * Card headers carry answer and correct-answer counts, which is the signal the
 * outline could not show.
 */
export const QuizQuestionsCanvas: React.FC<QuizQuestionsCanvasProps> = ({
  questions,
  disabled,
  onQuestionsChange,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingScrollKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    const key = pendingScrollKey.current;
    if (!key) return;
    const card = rootRef.current?.querySelector<HTMLElement>(
      `[data-question-key="${key}"]`
    );
    if (!card) return;
    pendingScrollKey.current = undefined;
    // jsdom has no scrollIntoView.
    if (card.scrollIntoView) card.scrollIntoView({block: 'nearest'});
  }, [questions]);

  const handleAddQuestion = () => {
    const question = CreateDefaultCourseDefinitionQuizQuestion();
    const key = getQuestionKey(question);
    pendingScrollKey.current = key;
    setExpandedKeys(current => [...current, key]);
    onQuestionsChange([...questions, question]);
  };

  const toggle = (key: string) =>
    setExpandedKeys(current =>
      current.includes(key)
        ? current.filter(item => item !== key)
        : [...current, key]
    );

  const handleDragEnd = (result: DropResult) => {
    const {destination, source} = result;
    if (!destination || destination.index === source.index) return;
    const items = Array.from(questions);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    onQuestionsChange(items);
  };

  const handleQuestionChange = (
    index: number,
    updated: CourseDefinitionQuizQuestionResponse
  ) => {
    onQuestionsChange(
      questions.map((question, position) =>
        position === index ? (updated as QuizQuestionWithKey) : question
      )
    );
  };

  const handleDelete = (index: number) => {
    onQuestionsChange(questions.filter((_, position) => position !== index));
  };

  return (
    <Stack gap="sm" ref={rootRef}>
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Questions
        </Text>
        <Text size="xs" c="dimmed">
          {questions.length} question{questions.length === 1 ? '' : 's'}
        </Text>
      </Group>

      {questions.length === 0 && (
        <Text size="sm" c="dimmed">
          This quiz has no questions yet.
        </Text>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="quiz-questions" isDropDisabled={disabled}>
          {provided => (
            <Stack
              gap="xs"
              ref={provided.innerRef}
              {...provided.droppableProps}>
              {questions.map((question, index) => {
                const key = getQuestionKey(question);
                const opened = expandedKeys.includes(key);
                const answers = question.answers ?? [];
                const correct = correctAnswerCount(question);

                return (
                  <Draggable
                    key={key}
                    draggableId={key}
                    index={index}
                    isDragDisabled={disabled}>
                    {draggableProvided => (
                      <Card
                        withBorder
                        p={0}
                        ref={draggableProvided.innerRef}
                        data-question-key={key}
                        {...draggableProvided.draggableProps}>
                        <Group
                          gap="xs"
                          wrap="nowrap"
                          p="xs"
                          onClick={() => toggle(key)}
                          style={{cursor: 'pointer'}}>
                          <Box
                            c="dimmed"
                            {...draggableProvided.dragHandleProps}
                            onClick={(event: React.MouseEvent) =>
                              event.stopPropagation()
                            }
                            aria-label={`Reorder question ${index + 1}`}>
                            <IconGripVertical size={16} />
                          </Box>
                          {opened ? (
                            <IconChevronDown size={16} />
                          ) : (
                            <IconChevronRight size={16} />
                          )}
                          <Text
                            size="sm"
                            fw={500}
                            style={{flex: 1, minWidth: 0}}
                            truncate="end">
                            {index + 1}.{' '}
                            {question.labelText || 'Untitled question'}
                          </Text>
                          <Badge
                            size="sm"
                            variant="light"
                            color={correct === 0 ? 'red' : 'gray'}
                            style={{textTransform: 'none'}}>
                            {answers.length} answer
                            {answers.length === 1 ? '' : 's'} · {correct}{' '}
                            correct
                          </Badge>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            disabled={disabled}
                            aria-label={`Delete question ${index + 1}`}
                            onClick={(event: React.MouseEvent) => {
                              event.stopPropagation();
                              handleDelete(index);
                            }}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>

                        <Collapse in={opened}>
                          <Box px="md" pb="md">
                            <EditCourseDefinitionQuestion
                              question={question}
                              index={index}
                              onQuestionChange={updated =>
                                handleQuestionChange(index, updated)
                              }
                            />
                          </Box>
                        </Collapse>
                      </Card>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <Group>
        <CreateButton onClick={handleAddQuestion} size="sm" disabled={disabled}>
          Add Question
        </CreateButton>
      </Group>
    </Stack>
  );
};
